import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { cn } from '../../utils.js'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label text */
  label?: string
  /** Helper text shown below the input */
  helperText?: string
  /** Error message (also sets aria-invalid) */
  error?: string
  /** Size preset */
  size?: 'sm' | 'md' | 'lg'
}

const sizeClasses = {
  sm: 'px-2.5 py-1.5 text-sm',
  md: 'px-3 py-2 text-sm',
  lg: 'px-4 py-2.5 text-base',
}

/**
 * Accessible text input with label, helper text, and error state.
 * Supports text, email, password, number, and tel types.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, helperText, error, size = 'md', className, id: externalId, ...props },
  ref,
) {
  const generatedId = useId()
  const id = externalId ?? generatedId
  const helperId = `${id}-helper`
  const errorId = `${id}-error`
  const hasError = Boolean(error)

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={id}
          className="text-sm font-medium text-[var(--color-text)]"
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={id}
        aria-invalid={hasError}
        aria-describedby={
          [error ? errorId : null, helperText ? helperId : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        className={cn(
          'w-full rounded-[var(--radius-md)] border bg-[var(--color-background)] text-[var(--color-text)]',
          'placeholder:text-[var(--color-text-muted)]',
          'focus:outline-none focus:ring-2 focus:ring-offset-0',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'transition-colors',
          hasError
            ? 'border-[var(--color-error)] focus:ring-[var(--color-error)]'
            : 'border-[var(--color-border)] focus:ring-[var(--color-primary)]',
          sizeClasses[size],
          className,
        )}
        {...props}
      />
      {error && (
        <p id={errorId} role="alert" className="text-sm text-[var(--color-error)]">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p id={helperId} className="text-sm text-[var(--color-text-secondary)]">
          {helperText}
        </p>
      )}
    </div>
  )
})
